/**
 * Client chats (Aziz's team, 2026-09-28): the conversations WITH each clinic, in their own
 * place, apart from the team's rooms. Everything here is what the clinic sees in its
 * portal; what the team writes here goes to the clinic. Internal talk stays on Chats.
 */
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2 } from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import { ThreadChat } from '@/components/creative/ChatBox';
import { fetchChats, fetchClient, qk } from '@/lib/queries';
import { bidi } from '@/lib/bidi';
import { cn } from '@/lib/cn';

function ago(iso?: string | null) {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(m, 1)}m`;
  if (m < 60 * 24) return `${Math.round(m / 60)}h`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function ClientChatsPage() {
  const q = useQuery({ queryKey: ['chats'], queryFn: fetchChats, refetchInterval: 15000 });
  const rows = (q.data ?? []).filter((c) => c.kind === 'clinic')
    .sort((a, b) => (b.last_at ? new Date(b.last_at).getTime() : 0) - (a.last_at ? new Date(a.last_at).getTime() : 0));
  const unread = rows.reduce((n, c) => n + c.unread, 0);
  return (
    <div className="max-w-4xl">
      <PageHeader title="Client chats" subtitle={unread ? `${unread} unread from clinics` : 'Your conversations with each clinic. They see everything here; internal talk stays on Chats.'} />
      {q.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {!q.isLoading && rows.length === 0 && <Card><p className="text-sm text-muted">No clinics on your list yet.</p></Card>}
      <Card className="p-0 overflow-hidden">
        <ul className="divide-y divide-slate-800" data-testid="client-chat-list">
          {rows.map((c) => (
            <li key={c.thread}>
              <Link to={`/client-chats/${c.client_id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-800/40" data-testid="client-chat-row">
                <span className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-muted shrink-0"><Building2 size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block text-sm truncate', c.unread ? 'text-muted-strong font-semibold' : 'text-muted-strong')}>{c.title}</span>
                  <span className="block text-xs text-muted truncate" dir="auto">
                    {c.last_body ? <>{c.last_author ? `${c.last_author}: ` : ''}{bidi(c.last_body)}</> : 'No messages yet'}
                  </span>
                </span>
                <span className="text-right shrink-0">
                  <span className="block text-[11px] text-muted">{ago(c.last_at)}</span>
                  {c.unread > 0 && <span className="inline-block mt-1 min-w-[1.25rem] px-1.5 rounded-full bg-accent-500 text-white text-[10px] text-center">{c.unread}</span>}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/** /client-chats/:id : the conversation with one clinic, full height. */
export function ClientChatPage() {
  const { id = '' } = useParams<{ id: string }>();
  const client = useQuery({ queryKey: qk.client(id), queryFn: () => fetchClient(id), enabled: !!id });
  if (!id) return <Navigate to="/client-chats" replace />;
  return (
    <div className="max-w-4xl">
      <Link to="/client-chats" className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3"><ArrowLeft size={12} /> All client chats</Link>
      <PageHeader title={client.data?.name ?? 'Clinic'} subtitle="The clinic sees every message here in its portal and can answer. For team-only talk, use the clinic room on Chats." />
      <ThreadChat kind="clinic" id={id} tall defaultOpen />
    </div>
  );
}
